import { useEffect, useMemo } from "react";
import { Modal, Image } from "antd";

const imageTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const pdfTypes = ["application/pdf"];

const getFileName = (file) => {
  if (typeof file === "string") return file;
  return file?.name || "";
};

const isImageFile = (file) => {
  if (typeof file === "string") return /\.(png|jpg|jpeg|webp)(\?.*)?$/i.test(file);
  if (imageTypes.includes(file?.type)) return true;
  return /\.(png|jpg|jpeg|webp)$/i.test(file?.name || "");
};

const isPdfFile = (file) => {
  if (typeof file === "string") return /\.pdf(\?.*)?$/i.test(file);
  if (pdfTypes.includes(file?.type)) return true;
  return /\.pdf$/i.test(file?.name || "");
};

/** Append PDF viewer flags that hide download/print toolbar (Chromium). */
const toPreviewOnlyPdfUrl = (url) => {
  if (!url) return null;
  const [base] = url.split("#");
  return `${base}#toolbar=0&navpanes=0&scrollbar=0`;
};

const FilePreviewModal = ({
  open,
  onClose,
  file,
  title = "Document Preview",
}) => {
  const isImage = isImageFile(file);
  const isPdf = isPdfFile(file);

  const objectUrl = useMemo(() => {
    if (!open || !file || typeof file === "string") return null;
    return URL.createObjectURL(file);
  }, [open, file]);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const previewUrl =
    typeof file === "string" ? file : objectUrl;

  const pdfSrc = isPdf ? toPreviewOnlyPdfUrl(previewUrl) : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      title={title || getFileName(file) || "Document Preview"}
      centered
      destroyOnHidden
      styles={{ body: { paddingTop: 12 } }}
    >
      {isImage && previewUrl && (
        <Image src={previewUrl} width="100%" preview={false} />
      )}

      {isPdf && pdfSrc && (
        <iframe
          src={pdfSrc}
          title="PDF Preview"
          width="100%"
          height="480px"
          style={{ border: 0 }}
        />
      )}

      {!isImage && !isPdf && (
        <div style={{ textAlign: "center", padding: 40 }}>
          Preview not available for this file type.
        </div>
      )}
    </Modal>
  );
};

export default FilePreviewModal;
